import React, { useRef, useState, useEffect } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Replay } from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DataTable, Column } from '../../../common/DataTable';
import { Pagination } from '../../../common/DataTable/Pagination';
import {
  WebhookEventDataType,
  WebhookEndpoint,
  SortByValue,
  EventStatusDisplayValue,
  EventStatusValue,
  WebhookEventPageDataType,
  RetryWebhookEventOptions,
  WebhookEventsSortQuery,
  WebhookRetryConflictError,
} from '../types/types';
import WebhookEventRetryModal from './WebhookEventRetryModal';
import { eventDeliveryDiffersFromSubscription } from '../utils/eventDeliveryDiff';
import { useMerchant } from '../../../contexts/MerchantContext';
import { SortQuery } from '../../../store/modules/paylink/types';
import { Button } from '../../../components/core';
import { WebhookStatusChip } from './WebhookStatusChip';
import { WebhookEventResponsesDialog } from './WebhookEventResponsesDialog';
import { TruncatedUrlCell } from './TruncatedUrlCell';
import { toast } from '../../../util/toast';

/** This allows dayjs to work with UTC */
dayjs.extend(utc);

/** Formats a UTC datetime for event log table columns */
const formatEventLogDateTime = (value?: string | null) =>
  value ? dayjs.utc(value).format('MM/DD/YY h:mm:ss a') : '—';

const WEBHOOK_EVENTS_SORT_FIELDS = ['eventStatus', 'lastAttempt', 'initialSend'] as const;

const isWebhookEventsSortField = (
  value: string | null | undefined,
): value is (typeof WEBHOOK_EVENTS_SORT_FIELDS)[number] =>
  WEBHOOK_EVENTS_SORT_FIELDS.some((field) => field === value);

const canRetryWebhookEvent = (row: WebhookEventDataType, isLocallyRetrying: boolean) =>
  !isLocallyRetrying &&
  row.eventStatus !== EventStatusValue.IN_PROCESS &&
  (row.displayStatus === EventStatusDisplayValue.FAILURE ||
    row.displayStatus === EventStatusDisplayValue.PENDING);

const getPendingDeliveryMessage = (row: WebhookEventDataType) =>
  row.eventStatus === EventStatusValue.AUTO_RETRYING ? 'Attempting retry...' : 'Sending request...';

type WebhookEventTableProps = {
  /** Events to display in the table */
  events: WebhookEventDataType[];
  /** Page data for webhook events */
  pageData: WebhookEventPageDataType;
  /** Current sort query */
  sortQuery: WebhookEventsSortQuery | undefined;
  /** Whether or not data for the table is loading */
  isDataLoading: boolean;
  /** Updates the current page number (0-based) */
  setEventsPageNumber: (pageNumber: number) => void;
  /** Updates page size and resets to the first page */
  setEventsPageSize: (pageSize: number) => void;
  /** Updates sort and resets to the first page */
  setEventsSortQuery: (sortQuery: WebhookEventsSortQuery | undefined) => void;
  /** Active webhook subscriptions for diff detection */
  webhooks: WebhookEndpoint[];
  /** Retries webhook events */
  retryWebhookEvent: (
    eventId: string,
    eventMerchantId?: string,
    options?: RetryWebhookEventOptions,
  ) => Promise<WebhookEventDataType>;
};

/** Displays the webhook events in a table */
const WebhookEventTable = (props: WebhookEventTableProps) => {
  const { canUpdateMerchantSettings } = useMerchant();
  const {
    events,
    pageData,
    sortQuery,
    isDataLoading,
    setEventsPageNumber,
    setEventsPageSize,
    setEventsSortQuery,
    webhooks,
    retryWebhookEvent,
  } = props;

  const tableRef = useRef<HTMLDivElement>(null);
  const [retryEvent, setRetryEvent] = useState(Array(events.length).fill(false));
  const [selectedEvent, setSelectedEvent] = useState<WebhookEventDataType | null>(null);
  const [retryModalEvent, setRetryModalEvent] = useState<WebhookEventDataType | null>(null);
  const [retryModalSubscription, setRetryModalSubscription] = useState<WebhookEndpoint | null>(
    null,
  );
  const [isResponsesDialogOpen, setIsResponsesDialogOpen] = useState(false);
  const { pageNumber, pageSize } = pageData;

  const openResponsesDialog = (event: WebhookEventDataType) => {
    setSelectedEvent(event);
    setIsResponsesDialogOpen(true);
  };

  const closeResponsesDialog = () => {
    setIsResponsesDialogOpen(false);
  };

  useEffect(() => {
    if (!isDataLoading) {
      // Reset state for retries when data is done loading
      setRetryEvent(Array(events.length).fill(false));
    }
  }, [isDataLoading, events.length]);

  const getEventIndex = (eventId: string) => events.findIndex((event) => event.eventId === eventId);

  const setRetryingState = (index: number, isRetrying: boolean) => {
    setRetryEvent((prev) => {
      const updated = [...prev];
      updated[index] = isRetrying;
      return updated;
    });
  };

  const handleRetryResult = (updatedEvent: WebhookEventDataType) => {
    if (updatedEvent.displayStatus === EventStatusDisplayValue.SUCCESS) {
      toast.success('Webhook event was delivered successfully');
    } else {
      toast.error('Webhook delivery failed. View responses for details.');
    }
  };

  const executeRetry = async (
    index: number,
    eventId: string,
    eventMerchantId: string,
    options?: RetryWebhookEventOptions,
  ) => {
    setRetryingState(index, true);
    try {
      const updatedEvent = await retryWebhookEvent(eventId, eventMerchantId, options);
      handleRetryResult(updatedEvent);
    } catch (error) {
      if (error instanceof WebhookRetryConflictError) {
        toast.error('Delivery already in progress. Please wait a moment and try again.');
      } else {
        toast.error('An error occurred while retrying the webhook event. Please try again later.');
      }
    } finally {
      setRetryingState(index, false);
      setRetryModalEvent(null);
      setRetryModalSubscription(null);
    }
  };

  /** Handles Retry button click for failed events */
  const retryClick = async (row: WebhookEventDataType) => {
    const index = getEventIndex(row.eventId);
    const subscription = webhooks.find((webhook) => webhook.subscriptionId === row.subscriptionId);

    if (subscription && eventDeliveryDiffersFromSubscription(row, subscription)) {
      setRetryModalEvent(row);
      setRetryModalSubscription(subscription);
      return;
    }

    await executeRetry(index, row.eventId, row.merchantId);
  };

  const columns: Column<WebhookEventDataType>[] = [
    {
      id: 'status',
      headerName: 'Status',
      sortable: true,
      minWidth: 90,
      width: 95,
      maxWidth: 105,
      paddingRight: 4,
      sortName: 'eventStatus',
      renderCell: (row) => {
        const index = events.findIndex((event) => event.eventId === row.eventId);
        return <WebhookStatusChip webhookEvent={row} isRetrying={retryEvent[index]} />;
      },
    },
    {
      id: 'initialSend',
      headerName: 'Initial send (UTC)',
      sortable: true,
      sortName: 'initialSend',
      minWidth: 150,
      width: 158,
      maxWidth: 165,
      paddingRight: 4,
      valueGetter: (row) => formatEventLogDateTime(row.created),
    },
    {
      id: 'lastAttempt',
      headerName: 'Last attempt (UTC)',
      sortable: true,
      sortName: 'lastAttempt',
      minWidth: 150,
      width: 158,
      maxWidth: 165,
      paddingRight: 4,
      valueGetter: (row) => formatEventLogDateTime(row.lastNotificationAttemptAt),
    },
    {
      id: 'endpointUrl',
      headerName: 'Endpoint URL',
      minWidth: 120,
      width: 225,
      maxWidth: 240,
      renderCell: (row) => <TruncatedUrlCell url={row.endpointUrl} />,
    },
    {
      id: 'event',
      headerName: 'Event',
      minWidth: 200,
      width: 218,
      maxWidth: 225,
      paddingRight: 4,
      valueGetter: (row) => row.eventType.eventDescription,
    },
    {
      id: 'responses',
      headerName: 'Responses',
      minWidth: 130,
      width: 145,
      maxWidth: 160,
      renderCell: (row) => {
        const index = events.findIndex((event) => event.eventId === row.eventId);
        const isLocallyRetrying = retryEvent[index];
        const isRetryEligible = canRetryWebhookEvent(row, isLocallyRetrying);
        const showPendingMessage =
          row.displayStatus === EventStatusDisplayValue.PENDING && !isRetryEligible;

        if (isLocallyRetrying || showPendingMessage) {
          return (
            <Typography variant="medium" fontStyle="italic">
              {getPendingDeliveryMessage(row)}
            </Typography>
          );
        }

        if (row.notificationAttemptCount === 0) {
          return (
            <Typography variant="medium" color="text.secondary">
              —
            </Typography>
          );
        }

        return (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => openResponsesDialog(row)}
            sx={{
              color: 'link.main',
              fontWeight: 600,
              textAlign: 'left',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            View responses
          </Link>
        );
      },
    },
    {
      id: 'retry',
      headerName: '',
      minWidth: 96,
      width: 96,
      maxWidth: 110,
      align: 'right',
      paddingRight: 8,
      renderCell: (row) => {
        const index = events.findIndex((event) => event.eventId === row.eventId);
        const isLocallyRetrying = retryEvent[index];

        if (!canRetryWebhookEvent(row, isLocallyRetrying)) {
          return null;
        }

        return (
          <Button
            onClick={() => {
              void retryClick(row);
            }}
            variant="text"
            startIcon={<Replay sx={{ transform: 'rotateY(180deg)' }} />}
            disabled={!canUpdateMerchantSettings()}
            text="Retry"
            sx={{ my: -2, whiteSpace: 'nowrap' }}
          />
        );
      },
    },
  ];

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setEventsPageNumber(page - 1);
    tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setEventsPageSize(newPageSize);
  };

  const handleSort = (sortQueryFromTable: SortQuery) => {
    const { sortBy: sortField, orderBy } = sortQueryFromTable;
    let nextSortQuery: WebhookEventsSortQuery | undefined;
    if (orderBy === 'asc' && isWebhookEventsSortField(sortField)) {
      nextSortQuery = {
        sortField,
        sortBy: SortByValue.ASC,
      };
    } else if (orderBy === 'desc' && isWebhookEventsSortField(sortField)) {
      nextSortQuery = {
        sortField,
        sortBy: SortByValue.DSC,
      };
    } else {
      nextSortQuery = undefined;
    }
    setEventsSortQuery(nextSortQuery);
  };

  const retryModalIndex = retryModalEvent ? getEventIndex(retryModalEvent.eventId) : -1;
  const isRetryModalRetrying = retryModalIndex >= 0 && retryEvent[retryModalIndex];

  return (
    <Box>
      {retryModalEvent && retryModalSubscription && (
        <WebhookEventRetryModal
          open
          event={retryModalEvent}
          subscription={retryModalSubscription}
          isRetrying={isRetryModalRetrying}
          onClose={() => {
            if (!isRetryModalRetrying) {
              setRetryModalEvent(null);
              setRetryModalSubscription(null);
            }
          }}
          onRetryWithSavedSettings={() => {
            void executeRetry(retryModalIndex, retryModalEvent.eventId, retryModalEvent.merchantId);
          }}
          onRetryWithCurrentSubscription={() => {
            void executeRetry(
              retryModalIndex,
              retryModalEvent.eventId,
              retryModalEvent.merchantId,
              { useCurrentSubscription: true },
            );
          }}
        />
      )}
      {selectedEvent && (
        <WebhookEventResponsesDialog
          isOpen={isResponsesDialogOpen}
          onClose={closeResponsesDialog}
          event={selectedEvent}
        />
      )}
      <DataTable
        rows={events}
        columns={columns}
        getRowId={(row) => row.eventId}
        isLoading={isDataLoading}
        pageSize={pageSize}
        sortCycle="toggle"
        sort={
          sortQuery?.sortField && sortQuery.sortBy
            ? {
                sortBy: sortQuery.sortField,
                orderBy: sortQuery.sortBy === SortByValue.ASC ? 'asc' : 'desc',
              }
            : {}
        }
        setSort={handleSort}
        tableHeight="calc(100vh - 315px)"
        noRowsMessage="No webhook events found. Try changing the filters or search term."
        tableRef={tableRef}
        rowSx={(row) => {
          const index = events.findIndex((event) => event.eventId === row.eventId);
          const isEventPending =
            row.displayStatus === EventStatusDisplayValue.PENDING || retryEvent[index];

          return {
            '& td > p': {
              color: isEventPending ? 'text.secondary' : 'text.primary',
              fontStyle: isEventPending ? 'italic' : 'normal',
            },
          };
        }}
        renderPagination={() => (
          <Pagination
            itemCount={events.length}
            itemTotal={pageData.totalRecords ?? 0}
            pageCount={pageData.totalPages ?? 0}
            onPageChange={handlePageChange}
            page={pageNumber + 1}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            disabled={isDataLoading}
          />
        )}
      />
    </Box>
  );
};

export default WebhookEventTable;

export const test = {
  UnconnectedWebhookEventTable: WebhookEventTable,
};
